import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useTasks } from '@/contexts/TaskContext';
import { Flag, AlertTriangle } from 'lucide-react';
import JobWorkCard from '@/components/JobWorkCard';
import JobCardSteps from '@/components/JobCardSteps';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PriorityTasks = () => {
  // Read from the global TaskContext (single source of truth).
  // activeTasks excludes archivedGlobally tasks and tasks from archived accounts.
  const { activeTasks: tasks, teamMembers, loading, refreshTasks } = useTasks();
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterMember, setFilterMember] = useState('all');

  const handleStatusUpdate = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus });
      toast.success('Status updated', {
        description: 'Synced across all sheets'
      });
      refreshTasks();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const getFilteredTasks = () => {
    let filtered = tasks.filter(task => !task.archived && !task.archivedGlobally).filter(task => {
      // Only show high priority or overdue tasks
      return task.priority === 'high' || task.isLagging;
    });

    if (filterPriority !== 'all') {
      filtered = filtered.filter(task => task.priority === filterPriority);
    }

    if (filterMember !== 'all') {
      // Check if member is in the responsible array (RACI) or assignedTo (legacy)
      filtered = filtered.filter(task => 
        task.responsible?.includes(filterMember) || task.assignedTo === filterMember
      );
    }

    // Sort by priority and due date
    return filtered.sort((a, b) => {
      // Lagging tasks first
      if (a.isLagging && !b.isLagging) return -1;
      if (!a.isLagging && b.isLagging) return 1;
      
      // Then by priority
      const priorityOrder = { high: 0, med: 1, low: 2 };
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      if (priorityDiff !== 0) return priorityDiff;
      
      // Then by due date
      if (a.dueDate && b.dueDate) {
        return new Date(a.dueDate) - new Date(b.dueDate);
      }
      return 0;
    });
  };

  // Helper to get member names from IDs
  const getMemberNames = (ids) => {
    if (!ids || ids.length === 0) return [];
    return ids.map(id => {
      const member = teamMembers.find(m => m.id === id);
      return member ? member.name : 'Unknown';
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const filteredTasks = getFilteredTasks();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="p-8 space-y-6"
      data-testid="priority-tasks-page"
    >
      <div>
        <h1 className="text-2xl sm:text-4xl font-bold tracking-tight">Priority Tasks</h1>
        <p className="text-muted-foreground mt-1">High-priority and overdue items requiring immediate attention</p>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="w-48">
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="bg-white" data-testid="filter-priority-select">
              <SelectValue placeholder="Filter by priority" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="high">High Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Select value={filterMember} onValueChange={setFilterMember}>
            <SelectTrigger className="bg-white" data-testid="filter-member-select">
              <SelectValue placeholder="Filter by member" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Members</SelectItem>
              {teamMembers.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  {member.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Priority Tasks Grid */}
      <div className="grid gap-4">
        {filteredTasks.map((task) => {
          return (
            <motion.div
              key={task.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              data-testid={`priority-task-${task.id}`}
            >
              <Card className={`border-border/60 hover:shadow-md transition-shadow ${
                task.isLagging ? 'border-l-4 border-l-error bg-error/5' : 'border-l-4 border-l-warning'
              }`}>
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="mt-1">
                      {task.isLagging ? (
                        <div className="inline-flex items-center gap-1 px-2 py-1 bg-red-500 text-white rounded-md text-xs font-bold animate-pulse">
                          <AlertTriangle className="h-3 w-3" />
                          OVERDUE
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1 px-2 py-1 bg-orange-500 text-white rounded-md text-xs font-bold">
                          <Flag className="h-3 w-3" />
                          HIGH PRIORITY
                        </div>
                      )}
                      {task.isLagging ? (
                        <AlertTriangle className="h-6 w-6 text-error" />
                      ) : (
                        <Flag className="h-6 w-6 text-warning" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold">{task.title}</h3>
                            {task.isLagging ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500 text-white rounded text-xs font-bold animate-pulse">
                                <AlertTriangle className="h-3 w-3" />
                                OVERDUE
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-500 text-white rounded text-xs font-bold">
                                <Flag className="h-3 w-3" />
                                HIGH PRIORITY
                              </span>
                            )}
                          </div>
                          {task.deliverable && (
                            <p className="text-sm text-muted-foreground mt-1">
                              Division: {task.deliverable}
                            </p>
                          )}
                          <JobWorkCard task={task} className="mt-1" />
                        </div>
                        <Select
                          value={task.status}
                          onValueChange={(value) => handleStatusUpdate(task.id, value)}
                        >
                          <SelectTrigger className="w-32 bg-white" data-testid={`status-select-${task.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="Yet to Start">Yet to Start</SelectItem>
                            <SelectItem value="In Progress">In Progress</SelectItem>
                            <SelectItem value="Done">Done</SelectItem>
                            <SelectItem value="On Hold">On Hold</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Responsible People */}
                      {task.responsible && task.responsible.length > 0 && (
                        <div className="flex items-center gap-2 mt-3">
                          <span className="text-xs font-semibold text-slate-500 uppercase">Responsible:</span>
                          <div className="flex items-center gap-2 flex-wrap">
                            {getMemberNames(task.responsible).map((name, idx) => (
                              <div key={idx} className="flex items-center gap-1.5 px-2 py-1 bg-blue-100 rounded-full">
                                <div className="h-5 w-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold">
                                  {name.charAt(0)}
                                </div>
                                <span className="text-sm font-medium text-blue-700">{name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-4 mt-4 text-sm">
                        {task.accountName && (
                          <span className="text-muted-foreground">
                            {task.accountName}
                          </span>
                        )}
                        {task.startDate && (
                          <span className="font-mono text-muted-foreground">
                            Start: {format(new Date(task.startDate), 'MMM dd, yyyy')}
                          </span>
                        )}
                        {task.dueDate && (
                          <span className={`font-mono ${
                            task.isLagging ? 'text-error font-semibold' : 'text-muted-foreground'
                          }`}>
                            Due: {format(new Date(task.dueDate), 'MMM dd, yyyy')}
                          </span>
                        )}
                      </div>

                      {task.isLagging && (
                        <div className="mt-3 p-3 bg-red-500 text-white border-2 border-red-600 rounded-md">
                          <p className="text-sm font-bold flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4" />
                            This task is overdue and requires immediate attention!
                          </p>
                        </div>
                      )}

                      {task.onHoldReason && task.status === 'On Hold' && (
                        <div className="mt-3 p-3 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
                          <p className="text-xs font-bold text-yellow-900 mb-1 uppercase">🛑 On Hold Reason:</p>
                          <p className="text-sm text-yellow-900 font-medium">{task.onHoldReason}</p>
                        </div>
                      )}

                      <JobCardSteps division={task.deliverable} job={task.job} className="mt-3" />
                      {task.instructions && (
                        <p className="text-sm text-muted-foreground mt-3 p-3 bg-muted/50 rounded-md">
                          {task.instructions}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {filteredTasks.length === 0 && (
        <Card className="border-border/60 bg-white">
          <div className="p-12 text-center">
            <Flag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No priority tasks found</p>
            <p className="text-sm text-muted-foreground mt-2">All high-priority items are under control</p>
          </div>
        </Card>
      )}
    </motion.div>
  );
};

export default PriorityTasks;
